import { useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { Lock, Loader2, ShieldCheck, User, Bell, BellRing, BellOff } from "lucide-react";
import { toast, Toaster } from "sonner";

export default function Account() {
  const { user } = useAuth();
  const { supported, permission, subscribed, loading: pushLoading, enable, disable } = usePushNotifications();
  const push = { supported, permission, subscribed, loading: pushLoading };
  const onEnable = enable;
  const onDisable = disable;
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.new_password.length < 6) {
      setError("New password must be at least 6 characters");
      return;
    }
    if (form.new_password !== form.confirm) {
      setError("New password and confirmation don't match");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Password updated. Keep it safe!");
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not change password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <Toaster theme="dark" position="top-right" richColors />

      <main className="mx-auto max-w-2xl w-full px-6 py-16 flex-1">
        <div className="overline mb-3">Account</div>
        <h1 className="font-display uppercase text-4xl sm:text-5xl text-chrome">Your Account</h1>
        <p className="mt-3 text-white/65">Manage your password and account info.</p>

        {/* Account summary */}
        <section className="mt-10 card-dark p-6" data-testid="account-summary">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-full grid place-items-center bg-gradient-to-br from-[#0055FF] to-[#00E5FF] text-white">
              {user?.role === "admin" ? <ShieldCheck size={18} /> : <User size={18} />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-varsity text-xl tracking-wider">{user?.name}</div>
              <div className="text-sm text-white/55 truncate">{user?.email}</div>
              <div className="mt-2 inline-flex items-center gap-2">
                <span className={`text-[10px] uppercase tracking-[0.2em] border px-2 py-0.5 rounded-full ${
                  user?.role === "admin"
                    ? "border-[#00E5FF]/40 bg-[#00E5FF]/10 text-[#00E5FF]"
                    : "border-white/15 text-white/60"
                }`}>
                  {user?.role === "admin" ? "Coach / Admin" : "Parent"}
                </span>
                {user?.dancer_name && (
                  <span className="text-xs text-white/55">Dancer: <span className="text-white/80">{user.dancer_name}</span></span>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Notifications */}
        <section className="mt-8 card-dark p-6" data-testid="account-notifications">
          <div className="flex items-center gap-2 mb-5">
            <Bell size={18} className="text-[#00E5FF]" />
            <h2 className="font-varsity text-xl tracking-wider">Notifications</h2>
          </div>

          <div className="flex items-start gap-4">
            <div className={`h-10 w-10 rounded-full grid place-items-center shrink-0 ${
              push.subscribed
                ? "bg-gradient-to-br from-[#0055FF] to-[#00E5FF] text-white"
                : "bg-white/5 text-white/40 border border-white/10"
            }`}>
              {push.subscribed ? <BellRing size={16} /> : <BellOff size={16} />}
            </div>
            <div className="flex-1">
              <div className="font-varsity text-base tracking-wider">
                {push.subscribed ? "Notifications are ON for this device" : "Notifications are off"}
              </div>
              <div className="text-xs text-white/55 mt-1 leading-relaxed">
                {!push.supported && "This browser does not support push notifications. Open this site in Safari (iOS) or Chrome (Android) — and on iPhone, add it to your home screen first."}
                {push.supported && push.permission === "denied" && "Notifications were blocked in your browser. Open your browser's site settings, allow notifications for this site, then refresh."}
                {push.supported && push.permission !== "denied" && !push.subscribed && "Turn on lock-screen pings for new messages from Coach, broadcasts, and announcements."}
                {push.supported && push.subscribed && "You'll get lock-screen pings on this device. Repeat on each device you want notified."}
              </div>
              <div className="mt-4">
                {push.supported && (
                  push.subscribed ? (
                    <button onClick={onDisable} disabled={push.loading} className="btn-ghost !text-xs" data-testid="disable-push-btn">
                      {push.loading ? <Loader2 className="animate-spin" size={13} /> : <><BellOff size={13} /> Turn Off on This Device</>}
                    </button>
                  ) : push.permission !== "denied" ? (
                    <button onClick={onEnable} disabled={push.loading} className="btn-electric !text-xs" data-testid="enable-push-btn">
                      {push.loading ? <Loader2 className="animate-spin" size={13} /> : <><BellRing size={13} /> Enable Notifications</>}
                    </button>
                  ) : null
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Change password */}
        <section className="mt-8 card-dark p-6">
          <div className="flex items-center gap-2 mb-5">
            <Lock size={18} className="text-[#00E5FF]" />
            <h2 className="font-varsity text-xl tracking-wider">Change Password</h2>
          </div>

          <form onSubmit={submit} className="space-y-5" data-testid="change-password-form">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-white/50">Current password</label>
              <input type="password" required autoComplete="current-password"
                     value={form.current_password} onChange={update("current_password")}
                     className="input-line mt-1"
                     data-testid="current-password-input" />
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-white/50">New password</label>
                <input type="password" required minLength={6} autoComplete="new-password"
                       value={form.new_password} onChange={update("new_password")}
                       className="input-line mt-1" placeholder="At least 6 characters"
                       data-testid="new-password-input" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-white/50">Confirm new password</label>
                <input type="password" required minLength={6} autoComplete="new-password"
                       value={form.confirm} onChange={update("confirm")}
                       className="input-line mt-1"
                       data-testid="confirm-password-input" />
              </div>
            </div>

            {error && (
              <div className="text-sm text-red-400 border border-red-400/30 bg-red-400/5 rounded px-3 py-2" data-testid="change-password-error">
                {error}
              </div>
            )}

            <div className="flex items-center gap-3">
              <button type="submit" disabled={saving} className="btn-electric" data-testid="change-password-submit">
                {saving ? <><Loader2 className="animate-spin" size={14} /> Updating…</> : "Update Password"}
              </button>
              <div className="text-[11px] text-white/40">
                Tip: pick something a parent wouldn&apos;t guess and you don&apos;t use elsewhere.
              </div>
            </div>
          </form>
        </section>
      </main>

      <Footer />
    </div>
  );
}
